import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import FormRequestForm from 'Components/FormRequestsForm';
import UISpinner from 'Components/UIComponents/UISpinner';
import { useDocumentGuard, useTemplateReplicate } from 'Hooks/Document';
import { Document, DocumentStatuses } from 'Interfaces/Document';
import { AuthorizedRoutePaths } from 'Interfaces/RoutePaths';
import Toast from 'Services/Toast';
import { selectDocument } from 'Utils/selectors';

type FormRequestRouteParams = {
  formRequestId: Document['id'];
};

const FormRequestEdit = () => {
  const navigate = useNavigate();
  const params = useParams<FormRequestRouteParams>();

  const sourceTemplateId = params.formRequestId || '';
  const [templateId, setTemplateId] = useState<Document['id'] | undefined>();
  const template = useSelector(state =>
    selectDocument(state, { documentId: templateId }),
  );

  const [replicateTemplate, isReplicateLoading] = useTemplateReplicate();

  const handleTemplateCheckFailure = useCallback(() => {
    navigate(AuthorizedRoutePaths.FORM_REQUESTS, { replace: true });
  }, [navigate]);

  const handleTemplateCheckSuccess = useCallback(
    async (template: Document) => {
      try {
        let updatingTemplate = template;

        if (template.status !== DocumentStatuses.DRAFT) {
          updatingTemplate = (await replicateTemplate({
            documentId: sourceTemplateId,
          })) as Document;
        }

        setTemplateId(updatingTemplate.id);
      } catch (error) {
        Toast.handleErrors(error);
        handleTemplateCheckFailure();
      }
    },
    [handleTemplateCheckFailure, replicateTemplate, sourceTemplateId],
  );

  const isCheckingTemplate = useDocumentGuard({
    documentId: sourceTemplateId,
    onFailure: handleTemplateCheckFailure,
    onSuccess: handleTemplateCheckSuccess,
  });

  if (isCheckingTemplate || isReplicateLoading) {
    return <UISpinner wrapperClassName="spinner--main__wrapper" width={50} height={50} />;
  }

  if (!template) return null;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { fields, ...restTemplate } = template;

  return (
    <FormRequestForm initialValues={restTemplate} sourceTemplateId={sourceTemplateId} />
  );
};

export default FormRequestEdit;
